import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { CarouselShell } from '@/components/carousel/CarouselShell';
import { CustomerNameDialog } from '@/components/carousel/CustomerNameDialog';
import { PrizeCardDeck, type DeckPhase } from '@/components/carousel/PrizeCardDeck';
import { PrizeStats } from '@/components/carousel/PrizeStats';
import { WinCelebration } from '@/components/carousel/WinCelebration';
import {
  computePrizeStats,
  listCarouselPrizes,
  mapSpinError,
  spinCarouselPrize,
  type CarouselPrize,
  type SpinResult,
} from '@/lib/carousel-service';
import { unlockCarouselAudio } from '@/lib/carousel-sound';
import { isCarouselOnlyApp } from '@/lib/app-variant';
import { cn } from '@/lib/utils';
import { Gift, Loader2, Sparkles } from 'lucide-react';

type Stage = 'ready' | 'spinning' | 'celebrate';

/** How long the deck shuffles before the winner is shown. */
const SHUFFLE_MS = 12_000;

const RING_RADIUS = 46;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default function CarouselSpin() {
  const { companyId, userRole } = useAuth();
  const { toast } = useToast();
  const isAdmin = userRole === 'admin' && !isCarouselOnlyApp;

  const [prizes, setPrizes] = useState<CarouselPrize[]>([]);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [stage, setStage] = useState<Stage>('ready');
  const [deckPhase, setDeckPhase] = useState<DeckPhase>('idle');
  const [result, setResult] = useState<SpinResult | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [shuffleProgress, setShuffleProgress] = useState(0);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);

  const stats = useMemo(() => computePrizeStats(prizes), [prizes]);
  const activePrizes = useMemo(() => prizes.filter((p) => p.is_active), [prizes]);

  /** Only these can actually be won — drives the deck and the spin button. */
  const availablePrizes = useMemo(
    () => activePrizes.filter((p) => p.remaining_quantity > 0),
    [activePrizes]
  );

  /**
   * The showcase list. Unlike `availablePrizes` it keeps prizes that have run
   * out, so they can be shown greyed instead of silently disappearing.
   * Big prizes stay pinned to the top; within a group, still-available first.
   */
  const catalogPrizes = useMemo(
    () =>
      [...activePrizes].sort((a, b) => {
        if (a.is_big_prize !== b.is_big_prize) return a.is_big_prize ? -1 : 1;
        const aOut = a.remaining_quantity === 0;
        const bOut = b.remaining_quantity === 0;
        if (aOut !== bOut) return aOut ? 1 : -1;
        return a.name.localeCompare(b.name, 'tr');
      }),
    [activePrizes]
  );

  const canSpin = availablePrizes.length > 0 && stats.remaining > 0;

  const loadPrizes = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const rows = await listCarouselPrizes(companyId);
      setPrizes(rows);
    } catch (error) {
      toast({
        title: 'Ödüller yüklenemedi',
        description: mapSpinError(error),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, toast]);

  useEffect(() => {
    void loadPrizes();
  }, [loadPrizes]);

  useEffect(() => {
    if (deckPhase !== 'shuffling') {
      setShuffleProgress(0);
      return;
    }

    const started = Date.now();
    let frame = 0;
    const tick = () => {
      setShuffleProgress(Math.min(1, (Date.now() - started) / SHUFFLE_MS));
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frame);
  }, [deckPhase]);

  const secondsLeft = Math.max(0, Math.ceil((SHUFFLE_MS * (1 - shuffleProgress)) / 1000));

  const handleSpin = async () => {
    if (!companyId || spinning || !canSpin) return;
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: 'Eksik bilgi',
        description: 'Lütfen müşteri adı ve soyadını girin.',
        variant: 'destructive',
      });
      return;
    }

    // Browsers only allow audio after a gesture — this call is inside the click.
    unlockCarouselAudio();
    setSpinning(true);
    setStage('spinning');
    setDeckPhase('shuffling');
    setResult(null);

    try {
      const spinPromise = spinCarouselPrize(companyId, firstName, lastName);
      const minShuffle = new Promise<void>((resolve) => {
        window.setTimeout(resolve, SHUFFLE_MS);
      });

      const [spinResult] = await Promise.all([spinPromise, minShuffle]);
      setResult(spinResult);
      setDeckPhase('flipping');
    } catch (error) {
      setStage('ready');
      setDeckPhase('idle');
      setSpinning(false);
      toast({
        title: 'Çekim başarısız',
        description: mapSpinError(error),
        variant: 'destructive',
      });
      await loadPrizes();
    }
  };

  const handleRevealComplete = useCallback(() => {
    setDeckPhase('revealed');
    window.setTimeout(() => {
      setStage('celebrate');
      setSpinning(false);
    }, 900);
  }, []);

  const handleContinue = useCallback(async () => {
    setResult(null);
    setFirstName('');
    setLastName('');
    setStage('ready');
    setDeckPhase('idle');
    await loadPrizes();
  }, [loadPrizes]);

  return (
    <CarouselShell variant="dark">
      <div className="relative h-full overflow-hidden bg-slate-950">
        {/* Stage backdrop */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(251,191,36,0.22),transparent_60%),radial-gradient(ellipse_60%_50%_at_10%_100%,rgba(99,102,241,0.22),transparent_60%),radial-gradient(ellipse_60%_50%_at_90%_80%,rgba(244,63,94,0.14),transparent_60%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]" />

        {/* Narrow screens scroll; from lg up everything is locked to one screen. */}
        <div className="relative z-10 h-full overflow-y-auto lg:overflow-hidden">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
            </div>
          ) : (
            // h-full only from lg up: that's where the one-screen kiosk layout
            // applies. Below lg the column must size to its content so the
            // parent scrolls, instead of compressing the prize tiles.
            <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-5 p-4 lg:h-full lg:flex-row lg:items-stretch lg:gap-8 lg:p-6 xl:gap-10 xl:p-8">
              {/* ---- Stage column: the deck is the hero ---- */}
              <div className="flex min-w-0 flex-1 flex-col lg:min-h-0">
                {/* Short screens drop the kicker and strapline so the deck and
                    the spin button both stay on screen. */}
                <header className="shrink-0 space-y-1.5 text-center [@media(max-height:820px)]:space-y-0.5">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300 ring-1 ring-amber-400/25 [@media(max-height:820px)]:hidden">
                    <Sparkles className="h-3.5 w-3.5" />
                    Mağaza Çekilişi
                  </span>
                  <h1 className="bg-gradient-to-b from-white via-amber-100 to-amber-400 bg-clip-text text-3xl font-black tracking-tight text-transparent drop-shadow-sm md:text-5xl 2xl:text-6xl [@media(max-height:820px)]:text-3xl">
                    Ödül Kartları
                  </h1>
                  <p className="text-sm text-slate-400 [@media(max-height:820px)]:hidden">
                    Kartlar karışır, kazanan kart açılır
                  </p>
                </header>

                {stage !== 'celebrate' && (
                  <PrizeCardDeck
                    prizes={availablePrizes}
                    result={result}
                    phase={deckPhase}
                    shuffleMs={SHUFFLE_MS}
                    onRevealComplete={handleRevealComplete}
                  />
                )}

                <div className="flex shrink-0 items-start justify-center pt-2">
                  {deckPhase === 'shuffling' ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative h-24 w-24 2xl:h-28 2xl:w-28">
                        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                          <circle
                            cx="50"
                            cy="50"
                            r={RING_RADIUS}
                            fill="none"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="5"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r={RING_RADIUS}
                            fill="none"
                            stroke="url(#carousel-ring)"
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeDasharray={RING_CIRCUMFERENCE}
                            strokeDashoffset={RING_CIRCUMFERENCE * shuffleProgress}
                          />
                          <defs>
                            <linearGradient id="carousel-ring" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor="#fcd34d" />
                              <stop offset="100%" stopColor="#f59e0b" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-black tabular-nums text-amber-300 2xl:text-4xl">
                            {secondsLeft}
                          </span>
                        </div>
                      </div>
                      <p className="animate-pulse text-lg font-semibold tracking-wide text-slate-200">
                        Kartlar karıştırılıyor...
                      </p>
                    </div>
                  ) : stage === 'ready' ? (
                    <div className="w-full max-w-md space-y-3 text-center">
                      <Button
                        size="lg"
                        disabled={!canSpin}
                        onClick={() => {
                          unlockCarouselAudio();
                          setNameDialogOpen(true);
                        }}
                        className="group relative h-16 w-full overflow-hidden rounded-2xl bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 text-xl font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-transform hover:from-amber-200 hover:via-amber-300 hover:to-amber-400 active:scale-[0.98] disabled:from-slate-700 disabled:via-slate-700 disabled:to-slate-700 disabled:text-slate-500 disabled:shadow-none [@media(max-height:820px)]:h-14"
                      >
                        {canSpin && (
                          <span className="carousel-cta-shine pointer-events-none absolute inset-y-0 w-1/3 bg-white/40" />
                        )}
                        <Sparkles className="relative mr-2 h-6 w-6" />
                        <span className="relative">Çekilişe Başla</span>
                      </Button>
                      <p className="text-sm text-slate-400 [@media(max-height:820px)]:hidden">
                        {canSpin
                          ? 'Müşterinin adını girin, kartlar karışsın'
                          : 'Şu anda dağıtılacak ödül bulunmuyor.'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Sparkles className="h-8 w-8 animate-pulse text-amber-300" />
                      <p className="text-lg font-semibold text-amber-200">
                        Kazanan kart açılıyor...
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ---- Side panel: stats + the prize catalogue ---- */}
              <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[330px] lg:min-h-0 xl:w-[380px] 2xl:w-[430px]">
                <PrizeStats stats={stats} />

                <section className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
                  <div className="flex shrink-0 items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Çekilişteki Ödüller
                    </h2>
                    <span className="rounded-full bg-white/5 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-white/10">
                      {availablePrizes.length} çeşit
                    </span>
                  </div>

                  {catalogPrizes.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.03] p-10 text-center text-slate-400">
                      Dağıtılacak aktif ödül kalmadı.
                      {isAdmin && (
                        <div className="mt-4">
                          <Button
                            asChild
                            variant="outline"
                            className="rounded-xl border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white"
                          >
                            <Link to="/carousel/admin">Ödül ekle</Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Only this panel scrolls, so the stage never moves.
                    // auto-rows-max + content-start are required: the grid has a
                    // definite height from flex-1, and without them it divides that
                    // height across the rows, crushing the tiles instead of scrolling.
                    <div className="grid auto-rows-max content-start grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
                    {catalogPrizes.map((prize) => {
                      const given = prize.total_quantity - prize.remaining_quantity;
                      const pct =
                        prize.total_quantity > 0
                          ? Math.round((given / prize.total_quantity) * 100)
                          : 0;
                      const isExhausted = prize.remaining_quantity === 0;

                      return (
                        <article
                          key={prize.id}
                          className={cn(
                            'group overflow-hidden rounded-2xl ring-1 transition-all duration-300',
                            isExhausted
                              ? 'bg-white/[0.02] opacity-45 grayscale ring-white/5'
                              : 'bg-white/[0.04] ring-white/10 hover:-translate-y-1 hover:bg-white/[0.07] hover:ring-amber-300/30'
                          )}
                        >
                          {/* Light tile + object-contain so product shots are never cropped. */}
                          <div className="relative aspect-square overflow-hidden bg-white">
                            {prize.image_url ? (
                              <img
                                src={prize.image_url}
                                alt={prize.name}
                                loading="lazy"
                                className={cn(
                                  'h-full w-full object-contain p-2.5 transition-transform duration-500',
                                  !isExhausted && 'group-hover:scale-105'
                                )}
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                                <Gift className="h-10 w-10 text-slate-400" />
                              </div>
                            )}
                            {prize.is_big_prize && (
                              <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-900 shadow-lg">
                                <Sparkles className="h-3 w-3" />
                                Büyük
                              </span>
                            )}
                            {isExhausted ? (
                              <span className="absolute bottom-2 right-2 rounded-full bg-slate-900/85 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-300 shadow-sm ring-1 ring-white/10">
                                Tükendi
                              </span>
                            ) : (
                              <span className="absolute bottom-2 right-2 rounded-full bg-slate-900/85 px-2 py-0.5 text-[11px] font-bold tabular-nums text-amber-200 shadow-sm ring-1 ring-white/10">
                                {prize.remaining_quantity} kaldı
                              </span>
                            )}
                          </div>

                          <div className="space-y-2 p-3">
                            <p className="line-clamp-2 text-sm font-semibold leading-snug text-slate-100">
                              {prize.name}
                            </p>
                            <div className="h-1 overflow-hidden rounded-full bg-white/10">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-[width] duration-500',
                                  isExhausted
                                    ? 'bg-slate-500'
                                    : 'bg-gradient-to-r from-amber-300 to-amber-500'
                                )}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className="text-[11px] text-slate-500">
                              {given} / {prize.total_quantity} verildi
                            </p>
                          </div>
                        </article>
                      );
                    })}
                    </div>
                  )}
                </section>
              </aside>
            </div>
          )}
        </div>
      </div>

      <CustomerNameDialog
        open={nameDialogOpen && stage === 'ready'}
        onClose={() => setNameDialogOpen(false)}
        firstName={firstName}
        lastName={lastName}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        loading={spinning}
        onSubmit={() => {
          setNameDialogOpen(false);
          void handleSpin();
        }}
      />

      {stage === 'celebrate' && result && (
        <WinCelebration result={result} onContinue={() => void handleContinue()} />
      )}
    </CarouselShell>
  );
}
